// Dependency-free region proposer for high-contrast scenes (dark parts on a
// light background, e.g. counting components on a table/conveyor). It thresholds
// the already-downscaled analysis frame, groups connected foreground pixels
// (4-connectivity flood fill), and returns normalized bounding boxes filtered by
// area and aspect ratio. Same output shape as an OpenCV.js contour proposer, so
// it can be swapped later without touching callers.
//
// segmentBlobs(imageData, opts) -> [{ x, y, w, h }]  (all normalized 0-1)
export function segmentBlobs(imageData, opts = {}) {
    const { data, width: w, height: h } = imageData
    if (!w || !h) return []
    // Foreground = pixels darker than `threshold` (0-1 of full luma). Set
    // `invert` for light parts on a dark background.
    const threshold = opts.threshold != null ? opts.threshold : 0.45
    const invert = !!opts.invert
    const minAreaFrac = opts.minAreaFrac != null ? opts.minAreaFrac : 0.0006
    const maxAreaFrac = opts.maxAreaFrac != null ? opts.maxAreaFrac : 0.2
    const maxBlobs = opts.maxBlobs || 300

    const total = w * h
    const thr = threshold * 255
    const minArea = minAreaFrac * total
    const maxArea = maxAreaFrac * total

    const fg = new Uint8Array(total)
    for (let i = 0; i < total; i++) {
        const p = i * 4
        const lum = (data[p] * 299 + data[p + 1] * 587 + data[p + 2] * 114) / 1000
        const dark = lum < thr
        fg[i] = (invert ? !dark : dark) ? 1 : 0
    }

    const visited = new Uint8Array(total)
    const stack = new Int32Array(total)
    const boxes = []
    for (let start = 0; start < total; start++) {
        if (!fg[start] || visited[start]) continue
        let sp = 0
        stack[sp++] = start
        visited[start] = 1
        let minX = w
        let minY = h
        let maxX = 0
        let maxY = 0
        let area = 0
        while (sp > 0) {
            const idx = stack[--sp]
            const x = idx % w
            const y = (idx / w) | 0
            area++
            if (x < minX) minX = x
            if (x > maxX) maxX = x
            if (y < minY) minY = y
            if (y > maxY) maxY = y
            if (x > 0) {
                const n = idx - 1
                if (fg[n] && !visited[n]) {
                    visited[n] = 1
                    stack[sp++] = n
                }
            }
            if (x < w - 1) {
                const n = idx + 1
                if (fg[n] && !visited[n]) {
                    visited[n] = 1
                    stack[sp++] = n
                }
            }
            if (y > 0) {
                const n = idx - w
                if (fg[n] && !visited[n]) {
                    visited[n] = 1
                    stack[sp++] = n
                }
            }
            if (y < h - 1) {
                const n = idx + w
                if (fg[n] && !visited[n]) {
                    visited[n] = 1
                    stack[sp++] = n
                }
            }
        }
        if (area < minArea || area > maxArea) continue
        const bw = maxX - minX + 1
        const bh = maxY - minY + 1
        const aspect = bw / bh
        if (opts.minAspect && aspect < opts.minAspect) continue
        if (opts.maxAspect && aspect > opts.maxAspect) continue
        // Drop blobs hugging the frame border (usually vignette/edge artifacts,
        // not parts) — `edgeMargin` is a fraction of width/height.
        const em = opts.edgeMargin != null ? opts.edgeMargin : 0.01
        if (em > 0) {
            const mx = em * w
            const my = em * h
            if (minX <= mx || minY <= my || maxX >= w - 1 - mx || maxY >= h - 1 - my)
                continue
        }
        boxes.push({ x: minX / w, y: minY / h, w: bw / w, h: bh / h })
        if (boxes.length >= maxBlobs) break
    }
    return boxes
}
